// packages import
import { Box, Grid, Switch, Typography } from '@mui/material'
import { FC, useContext, useState } from 'react'
// component import
import { Alert } from '../common/Alert'
import { MfaAuthEnrollmentModal } from './MfaAuthEnrollmentModal'
// others
import { useUpdateMfaSettingsMutation } from '../../../generated'
import {
  MFA_AUTH_DESCRIPTION,
  MFA_AUTH_LABEL,
  MFA_DISABLED_SUCCESS,
  MFA_ENABLED_SUCCESS,
  MFA_ERROR_CODES,
  SOMETHING_WENT_WRONG,
  TWO_FACTOR_AUTHENTICATION
} from '../../constants'
import { AuthContext } from '../../context'
import { MfaSettingProps } from '../../interfaceTypes'
import palette from '../../theme/palette'
import { CardBox } from '../../theme/styleComponents'
import { caseDetailGrid } from '../../theme/styleConstants'

/**
 * Turns the user's own second factors on and off. Both may be active at once; when they
 * are, sign in uses the emailed code and the authenticator stays as backup — the server
 * decides that, this screen only says so.
 *
 * The authenticator cannot simply be switched on: it needs a secret shared with the
 * user's app first, so turning it on opens setup and the switch follows only once that
 * succeeds.
 */
export const MfaSetting: FC<MfaSettingProps> = ({ currentUser }) => {
  const { setUser } = useContext(AuthContext)
  const [isEnrolling, setIsEnrolling] = useState<boolean>(false)
  const { mfaAuthEnabled } = currentUser || {}

  /** Patches the two flags rather than replacing the user, so the rest of context survives. */
  const applyFlags = (nextEmailEnabled: boolean, nextAuthEnabled: boolean) =>
    currentUser && setUser({ ...currentUser, mfaEmailEnabled: nextEmailEnabled, mfaAuthEnabled: nextAuthEnabled })

  const [updateMfaSettings, { loading }] = useUpdateMfaSettingsMutation({
    onError({ message }) {
      // The global error link already explains a rejected change, and the switch is
      // driven by stored state rather than optimistic UI, so there is nothing to undo.
      // Only an unrecognised failure needs a message of its own.
      if (!MFA_ERROR_CODES.includes(message)) {
        Alert.error(SOMETHING_WENT_WRONG)
      }
    },

    onCompleted(data) {
      if (!data) return

      const { updateMfaSettings } = data
      const { response, user } = updateMfaSettings
      const { status } = response || {}

      if (status === 200 && user) {
        applyFlags(!!user.mfaEmailEnabled, !!user.mfaAuthEnabled)
        Alert.success(user.mfaEmailEnabled || user.mfaAuthEnabled ? MFA_ENABLED_SUCCESS : MFA_DISABLED_SUCCESS)
      }
    }
  })

  /**
   * Switching on means setting up first — the API refuses a bare flag with no secret
   * behind it. Switching off is a plain update, and discards the stored secret.
   */
  const handleAuthToggle = async (checked: boolean) => {
    if (checked) {
      setIsEnrolling(true)
      return
    }

    await updateMfaSettings({ variables: { mfaSettingsInput: { mfaAuthEnabled: false } } })
  }

  return (
    <CardBox mt={3}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        padding={3}
        borderBottom={`1px solid ${palette.lightGrayTwo}`}
      >
        <Typography variant="h6" fontSize="18px">
          {TWO_FACTOR_AUTHENTICATION}
        </Typography>
      </Box>

      {/* The "Email verification code" row is hidden at the client's request; only the
          authenticator is offered here. The email factor itself is untouched server-side. */}
      <Grid container sx={caseDetailGrid} alignItems="center">
        <Grid item md={9} sm={8} xs={12}>
          <Typography variant="h6" fontSize="16px">
            {MFA_AUTH_LABEL}
          </Typography>

          <Typography variant="subtitle1" component="p">
            {MFA_AUTH_DESCRIPTION}
          </Typography>
        </Grid>

        <Grid item md={3} sm={4} xs={12} textAlign="right">
          <Switch
            name="mfaAuthEnabled"
            checked={!!mfaAuthEnabled}
            disabled={loading}
            onChange={event => handleAuthToggle(event.target.checked)}
            inputProps={{ 'aria-label': MFA_AUTH_LABEL }}
          />
        </Grid>
      </Grid>

      <MfaAuthEnrollmentModal
        isOpen={isEnrolling}
        handleClose={() => setIsEnrolling(false)}
        onEnrolled={applyFlags}
      />
    </CardBox>
  )
}
